/* eslint-disable react/prop-types */
import { Box, CircularProgress, Typography } from "@mui/material";
import { Formik } from "formik";
import * as Sentry from "@sentry/react";
import React, { useState } from "react";
import Appointment from "../../../services/api/appointment";
import { debounce } from "../../../utils/helper";
import AppointMentBoolInput from "../../AppointMentBoolInput/AppointMentBoolInput";
import Button from "../../common/Button";
import { CommonStyles } from "../../common/Button/commonButtonStyle";
import Modal from "../../common/Modal/Modal";
import InputField from "../../common/new/InputField/InputField";
import styles from "./styles";
import { AlertStatus } from "../../../contants";

export const APPOINTMENT_RADIO = {
  DID_COMMITTEE_MEET: "didCommitteeMeet",
  DID_YOU_PARTICIPATE: "didYouParticipate",
  DO_YOU_APPROVE_APPOINTMENT: "doYouApproveAppointment",
};

const prepareBody = (formValues) => {
  return Object.keys(formValues).reduce((acc, key) => {
    return Object.assign(acc, { [key]: formValues[key] === "YES" });
  }, {});
};

const remarksValidation = (remark, values) => {
  if (!values[APPOINTMENT_RADIO.DID_COMMITTEE_MEET])
    return "Please select Did the committee Meet option";
  if (!values[APPOINTMENT_RADIO.DID_YOU_PARTICIPATE])
    return "Please select Did you participate option";
  if (!values[APPOINTMENT_RADIO.DO_YOU_APPROVE_APPOINTMENT])
    return "Please select Do you approve the Appointment option";
  if (!remark) return "Please enter remark";
  return "";
};

function Alert({ open, onClose, onSubmit, isLoading }) {
  return (
    <Box>
      <Modal open={open} onClose={onClose}>
        {isLoading ? (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "40px 20px",
              gap: "16px",
            }}
          >
            <CircularProgress size={48} />
            <Typography style={{ fontSize: "16px", color: "#626262" }}>
              Please wait...
            </Typography>
          </Box>
        ) : (
          <>
            <Typography
              style={{
                fontSize: "20px",
                fontWeight: "500",
                paddingBottom: "20px",
              }}
            >
              Are you sure you want to
              <br />
              force push this application ?
            </Typography>
            <Box
              style={{
                paddingTop: "50px",
                justifyContent: "space-between",
              }}
            >
              <Button
                onClick={onSubmit}
                label="Yes"
                style={{ marginRight: "10px" }}
                disabled={isLoading}
              />
              <Button onClick={onClose} label="No" variant="outlined" />
            </Box>
          </>
        )}
      </Modal>
    </Box>
  );
}

function ForcePushAppointmentApplication({
  application,
  open,
  onClose,
  toast,
  refreshList,
}) {
  const [remark, setRemark] = useState("");
  const [openAlert, setOpenAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  const toggleAlert = () => {
    if (isLoading) return;
    setOpenAlert(false);
  };

  const acceptAppointmentHandler = (values) => {
    const error = remarksValidation(remark, values);
    if (error) {
      toast.show(error, AlertStatus.ERROR);
      return;
    }
    setPendingValues(values);
    setOpenAlert(true);
  };

  const submitReviewRequest = async () => {
    try {
      setIsLoading(true);
      const body = prepareBody(pendingValues);

      await Appointment.forcePushAppointment(
        { ...body, doYouApproveAppointmentRemark: remark },
        application.memberDetails.memberId,
        application.id
      );

      toast.show("Successfully Done", AlertStatus.SUCCESS);
      setTimeout(() => {
        setIsLoading(false);
        setOpenAlert(false);
        onClose();
        refreshList();
      }, 2000);
    } catch (error) {
      setIsLoading(false);
      setOpenAlert(false);
      toast.show(error.message, AlertStatus.ERROR);
      Sentry.captureException(error);
    }
  };

  const handleModalClose = () => {
    if (isLoading) return;
    setRemark("");
    setPendingValues(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleModalClose}>
      <div
        style={{
          width: "60vw",
          maxWidth: "90vw",
          height: "auto",
          maxHeight: "80vh",
          minWidth: "320px",
          overflowY: "auto",
          padding: "20px",
        }}
      >
        <Formik initialValues={{}} enableReinitialize>
          {({ handleChange, values, errors }) => (
            <form>
              <Box>
                <Typography
                  style={{
                    color: "#626262",
                    fontSize: "20px",
                    fontWeight: "700",
                  }}
                >
                  Force Push Application
                </Typography>
                <Box marginTop="30px" color="#183053">
                  <Typography />
                </Box>
                <div>
                  <AppointMentBoolInput
                    name={APPOINTMENT_RADIO.DID_COMMITTEE_MEET}
                    value={values[APPOINTMENT_RADIO.DID_COMMITTEE_MEET]}
                    preLabel="Did the committee Meet ?"
                    handleChange={handleChange}
                    required
                    error={errors[APPOINTMENT_RADIO.DID_COMMITTEE_MEET]}
                  />
                  <AppointMentBoolInput
                    name={APPOINTMENT_RADIO.DID_YOU_PARTICIPATE}
                    value={values[APPOINTMENT_RADIO.DID_YOU_PARTICIPATE]}
                    preLabel="Did you participate ?"
                    handleChange={handleChange}
                    required
                    error={errors[APPOINTMENT_RADIO.DID_YOU_PARTICIPATE]}
                  />
                  <AppointMentBoolInput
                    name={APPOINTMENT_RADIO.DO_YOU_APPROVE_APPOINTMENT}
                    value={values[APPOINTMENT_RADIO.DO_YOU_APPROVE_APPOINTMENT]}
                    preLabel="Do you approve the Appointment ?"
                    handleChange={handleChange}
                    required
                    error={errors[APPOINTMENT_RADIO.DO_YOU_APPROVE_APPOINTMENT]}
                  />
                </div>
                <Box mt={7}>
                  <InputField
                    type="text"
                    defaultValue=""
                    key="remark"
                    name="remark"
                    label="Enter your remark here..."
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    InputProps={{ sx: { minHeight: "91px" } }}
                    style={styles.inputStyle}
                  />
                </Box>
                <div style={{ display: "flex", gap: 5 }}>
                  <Button
                    variant="contained"
                    style={CommonStyles.buttonStyle}
                    type="button"
                    label="Submit"
                    onClick={() => acceptAppointmentHandler(values)}
                  />
                  <Button
                    variant="outlined"
                    style={{ ...CommonStyles.buttonStyle, marginLeft: "20px" }}
                    onClick={handleModalClose}
                    label="CANCEL"
                  />
                </div>
              </Box>

              <Alert
                open={openAlert}
                onClose={toggleAlert}
                onSubmit={debounce(submitReviewRequest)}
                isLoading={isLoading}
              />
            </form>
          )}
        </Formik>
      </div>
    </Modal>
  );
}

export default ForcePushAppointmentApplication;
